/** Bump when the wire format changes incompatibly; the server rejects older clients with a refresh prompt. */
export const PROTOCOL_VERSION = 1;

/** Games bundled with the platform. New games add their id here and register a module in game-engine. */
export const GAME_IDS = ['panic-button', 'snapscramble', 'act-of-nonsense', 'wave-command', 'mercenary-market', 'orb-havoc', 'battle-bots', 'meteor-mayhem', 'lava-rising', 'objection', 'dealbreakers', 'filibuster', 'double-agent', 'the-alibi', 'conspiracy-club', 'terrible-advice', 'plot-twist', 'rotten-reviews', 'human-resources', 'boulder-bash', 'pizza-heist', 'recess-rumble', 'misquoted', 'initial-reaction', 'star-struck', 'grudge-gauntlet', 'phonenanigans'] as const;
export type GameId = (typeof GAME_IDS)[number];

/**
 * How finished a game is. Only "playable" games are on by default; "beta" games need a feature flag
 * (GMP_FEATURES on the server); "planned" games are menu cards only and can never be started.
 */
export type GameReadiness = 'playable' | 'beta' | 'planned';

/**
 * Party codes are 4 digits (easy to say, type on a number pad and read off a TV). The server allocates them
 * uniquely among open parties; in the unlikely case all 10,000 are taken it hands out 5 digits instead.
 * Parties created before the switch have 6-character codes from LEGACY_ROOM_CODE_ALPHABET (no 0/O, 1/I/L
 * or U/V lookalikes); those stay valid everywhere until the party closes.
 */
export const ROOM_CODE_DIGITS = 4;
export const ROOM_CODE_FALLBACK_DIGITS = 5;
export const LEGACY_ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTWXYZ2346789';
/** Longest code anywhere (legacy codes). */
export const ROOM_CODE_MAX_LENGTH = 6;
/** A complete code: 4 or 5 digits (current), or 6 letters/digits (legacy). Input must be normalized first. */
export const ROOM_CODE_PATTERN = /^(?:[0-9]{4,5}|[A-Z0-9]{6})$/;

export function normalizeRoomCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, ROOM_CODE_MAX_LENGTH);
}

export function isRoomCode(code: string): boolean {
  return ROOM_CODE_PATTERN.test(code);
}

/** Built-in avatars (drawn as vector characters by shared-ui). */
export const AVATAR_IDS = [
  'blob', 'bolt', 'cactus', 'crown', 'dice', 'donut', 'ghost', 'moon', 'octo', 'rocket', 'toast', 'cube',
  'cloud', 'flame', 'gem', 'mush',
] as const;
export type AvatarId = (typeof AVATAR_IDS)[number];

/** Player colors: assigned in join order, distinct hues with a shape marker so color is never the only cue. */
export const PLAYER_COLORS = [
  '#ff4d6d', '#ffb703', '#2ec4b6', '#7b61ff', '#3a86ff', '#ff7b00', '#06d6a0', '#f15bb5',
  '#9ef01a', '#00bbf9', '#e63946', '#fee440', '#8338ec', '#fb5607', '#4cc9f0', '#c77dff',
  '#80ed99', '#ffafcc', '#ffd166', '#118ab2',
] as const;

/** Computer players the host can add. Levels change how fast and how well they play, never what they can see. */
export const BOT_LEVELS = ['easy', 'normal', 'hard'] as const;
export type BotLevel = (typeof BOT_LEVELS)[number];

export const NICKNAME_MAX = 16;
export const MAX_ROOM_PLAYERS = 20;
